import React, { useState } from 'react';
import classes from './styles.module.scss';
import Card from '../../../../components/card/card.component';
import { useTranslation } from 'react-i18next';
import { useKato } from '../../../../contexts/kato.context';
import { ICardName, ITaxStats, KatoType } from '../../../../types/kato.types';
import { useNavigate, useParams } from 'react-router-dom';
import { useShared } from '../../../../contexts/shared.context';
import { useUser } from '../../../../contexts/user.context';

const baseClass = 'tax-card';
const additionalClass = 'budget-investment-projects-card';
const translationKey = 'cards.tax-card';

const defaultTabs: ITaxStats['type'][] = [
  'REGIONAL_BUDGET',
  'PROPER_REGIONAL_BUDGET',
];
const headers = [
  'income',
  'plan',
  'fact',
  'planPercent',
  'annual',
  'annualPercent',
];

const TaxCard = () => {
  const { t } = useTranslation();
  const {
    currentLevel,
    katoGeneralInfo: { taxStats, incomeStats },
  } = useKato();
  const { setFullscreen } = useShared();
  const navigate = useNavigate();
  const { modules } = useUser();
  const { kato, link, tab } = useParams<{
    kato: string;
    link: string;
    tab: string;
  }>();
  const [currentType, setCurrentType] = useState<ITaxStats['type']>(
    defaultTabs[0],
  );
  const onClick = () => {
    setFullscreen({ open: true, type: ICardName.TAX });
    navigate(`/${kato}/${link}/${tab}/${ICardName.TAX}`);
  };

  // const tabs: ITaxStats['type'][] = [KatoType.OBLAST, KatoType.REGION].includes(currentLevel) ? defaultTabs : ['REGIONAL_BUDGET'];

  const rows: { label: string; keys: (keyof ITaxStats)[] }[] = [
    {
      label: t(`${translationKey}.total`),
      keys: ['plan', 'fact', 'planPercent', 'annual', 'annualPercent'],
    },
    {
      label: t(`${translationKey}.tax`),
      keys: [
        'taxRevenuesPlan',
        'taxRevenuesFact',
        'taxRevenuesPlanPercent',
        'taxRevenuesAnnual',
        'taxRevenuesAnnualPercent',
      ],
    },
    {
      label: t(`${translationKey}.nonTax`),
      keys: [
        'nonTaxRevenuesPlan',
        'nonTaxRevenuesFact',
        'nonTaxRevenuesPlanPercent',
        'nonTaxRevenuesAnnual',
        'nonTaxRevenuesAnnualPercent',
      ],
    },
    {
      label: t(`${translationKey}.fixedAssets`),
      keys: [
        'fixedAssetsSalePlan',
        'fixedAssetsSaleFact',
        'fixedAssetsSalePlanPercent',
        'fixedAssetsSaleAnnual',
        'fixedAssetsSaleAnnualPercent',
      ],
    },
  ];

  const findData = (tab: string): ITaxStats => {
    return taxStats.find((item: any) => item.type === tab) || ({} as ITaxStats);
  };

  // const getType = (type: ITaxStats['type']) => {
  //     switch (currentLevel) {
  //         case KatoType.OBLAST:
  //             return type === 'REGIONAL_BUDGET' ? t(`${translationKey}.REGIONAL_BUDGET`) : t(`${translationKey}.PROPER_REGIONAL_BUDGET`);
  //         case KatoType.REGION:
  //             return type === 'REGIONAL_BUDGET' ? t(`${translationKey}.DISTRICT_BUDGET`) : t(`${translationKey}.PROPER_DISTRICT_BUDGET`);
  //         case KatoType.CITY:
  //             return t(`${translationKey}.CITY_BUDGET`);
  //         case KatoType.DISTRICT:
  //             return t(`${translationKey}.SO_BUDGET`);
  //         default:
  //             return t(`${translationKey}.REGIONAL_BUDGET`);
  //     }
  // }

  if (!taxStats || !taxStats.length || !incomeStats?.iframeUrl) return null;
  return (
    <Card
      title={t(`${translationKey}.title`)}
      subtitle={t(`${translationKey}.subtitle`, {
        unit: [KatoType.OBLAST, KatoType.REGION].includes(currentLevel)
          ? t('million')
          : t('thousand-short'),
      })}
      onClick={onClick}
    >
      {modules.includes('autolink') && incomeStats?.iframeUrl ? (
        <div className={classes[`${additionalClass}__iframe`]}>
          <iframe
            src={incomeStats?.iframeUrl}
            width="100%"
            height="100%"
          ></iframe>
        </div>
      ) : (
        <div
          className={`${classes[baseClass]} ${classes[`${additionalClass}`]}`}
        >
          <div className={classes[`${additionalClass}__radios`]}>
            {defaultTabs.map((type) => (
              <label
                key={type}
                className={`${classes[`${additionalClass}__radio`]} ${currentType === type ? classes['active'] : ''}`}
              >
                <input
                  type="radio"
                  name="budget-type"
                  checked={currentType === type}
                  onChange={() => setCurrentType(type)}
                />
                {t(`${translationKey}.${type}`)}
              </label>
            ))}
          </div>

          <div
            className={`${classes[`${baseClass}__table`]} ${classes[`${additionalClass}__table`]}`}
          >
            <table>
              <thead>
                <tr>
                  {headers.map((header, index) => (
                    <th key={index}>
                      <div className={classes[`${additionalClass}__header`]}>
                        <span>{t(`${translationKey}.${header}`)}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(({ label, keys }) => (
                  <tr key={label}>
                    <td>{label}</td>
                    {keys.map((key) => (
                      <td key={key}>
                        <span className={classes[`${baseClass}__value`]}>
                          {findData(currentType)
                            ?.[key]?.toLocaleString()
                            .replace('.', ',') ?? 0}
                          {key.includes('Percent') && '%'}
                        </span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
};

export default TaxCard;
